import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

import Tag from '@app/ui/sports-components/Tag';
import classNames from '@app/ui/sports-components/utils/classNames';
import substringMatch from '../../utils/substringMatch';
import './NestedSelect.scss';

const getTotalSelected = selected =>
    Object.values(selected).reduce((sum, v) => (v ? sum + 1 : sum), 0);

const xsTagFix = { top: '0.4rem', paddingRight: '2.25rem', right: '2rem' };
const smTagFix = { paddingRight: '2.5rem' };
const inputFix = { paddingRight: '7.25rem' };

// @TODO virtualize lists
// @TODO memoization
// @TODO add hint

const NestedSelect = ({
    onTextChange,
    disabled,
    onChange,
    selected,
    onBlur,
    options,
    expandable = false,
    context,
    placeholder = '',
    size = 'md',
    valueKey = 'value',
    labelKey = 'label',
    filter = substringMatch,
    loading = false,
    inline,
    onOpen = () => {},
    onClose = () => {},
    autoClearSearchValue = false,
}) => {
    const [isOpen, setOpen] = useState(false);
    const [isTagsOpen, setTagsOpen] = useState(false);
    const [nestedCount, setNestedCount] = useState({});
    const [searchTerm, setSearchTerm] = useState('');

    const initExpanded = useMemo(
        () =>
            options.reduce((acc, o) => {
                const showOpenAccordion =
                    o.nested &&
                    o.nested.length > 0 &&
                    o.nested.some(n => {
                        const newId = `${o.value}-${n.value}`;
                        return selected[newId];
                    });
                acc[o.value] = showOpenAccordion;
                return acc;
            }, {}),
        [selected],
    );

    const [isExpanded, setExpanded] = useState(initExpanded);
    const container = useRef(null);

    useEffect(() => {
        window.addEventListener('click', _closeOnClickOutside);
        return () => {
            window.removeEventListener('click', _closeOnClickOutside);
        };
    });

    useEffect(() => {
        if (isOpen) {
            onOpen();
        } else {
            onClose();
        }
    }, [isOpen]);

    useEffect(() => {
        const nestedCount = options.reduce((acc, option) => {
            acc[option.value] = option.nested
                ? option.nested.reduce((nestedAcc, nestedOption) => {
                      nestedAcc += selected[
                          `${option.value}-${nestedOption.value}`
                      ]
                          ? 1
                          : 0;
                      return nestedAcc;
                  }, 0)
                : 0;

            return acc;
        }, {});

        setNestedCount(nestedCount);
    }, [selected, options]);

    const _updateSearchTerm = ({ target }) => {
        setOpen(true);
        setSearchTerm(target.value);
        if (onTextChange) onTextChange(target.value);
    };

    const _openSelect = () => {
        if (isOpen) return;
        setOpen(true);
    };

    const _closeSelect = () => {
        setOpen(false);
        if (autoClearSearchValue) setSearchTerm('');
    };
    const _closeTags = () => setTagsOpen(false);

    const _toggleSelect = e => {
        e.preventDefault();
        setOpen(!isOpen);
        setExpanded(initExpanded);
    };

    const _toggleTags = () => setTagsOpen(isTagsOpen => !isTagsOpen);

    const _toggleExpanded = value => e => {
        e.stopPropagation();
        if (disabled || !expandable) return;
        setExpanded({
            ...isExpanded,
            [value]: !isExpanded[value],
        });
    };

    const _clearAll = e => {
        if (disabled) return;
        e.stopPropagation();
        onChange({});
        setNestedCount({});
    };

    const _toggleOption = (id, { hasChildren, nested }) => {
        if (disabled) return;
        const allSelected = nestedCount[id] === nested.length;

        const newSelected = {
            ...selected,
        };

        if (!hasChildren) {
            newSelected[id] = !selected[id];
        } else {
            nested.map(n => {
                const fakeId = `${id}-${n.value}`;
                newSelected[fakeId] = !allSelected;
            });
            setNestedCount({
                ...nestedCount,
                [id]: allSelected ? 0 : nested.length,
            });
        }

        onChange(newSelected);
        if (autoClearSearchValue) setSearchTerm('');
        if (onBlur) onBlur();
    };

    const _toggleNestedOption = (id, parentId) => {
        const fakeId = `${parentId}-${id}`;
        if (disabled) return;

        onChange({
            ...selected,
            [fakeId]: !selected[fakeId],
        });

        if (autoClearSearchValue) setSearchTerm('');

        const count =
            typeof nestedCount[parentId] === 'number'
                ? nestedCount[parentId]
                : 0;

        setNestedCount({
            ...nestedCount,
            [parentId]: selected[fakeId] ? count - 1 : count + 1,
        });
    };

    const _closeOnClickOutside = e => {
        if (container.current.contains(e.target)) return;
        _closeSelect();
        _closeTags();
    };

    const _selectAll = () => {
        if (disabled) return;
        const newNestedCount = {};

        const newOptions = options.reduce((acc, o) => {
            if (o.nested) {
                o.nested.map(n => {
                    const fakeId = `${o.value}-${n.value}`;
                    newNestedCount[o.value] = o.nested.length;
                    acc[fakeId] = true;
                });
            } else {
                acc[o.value] = true;
            }
            return acc;
        }, {});

        onChange(newOptions);
        setNestedCount(newNestedCount);
    };

    const selectedCount = getTotalSelected(selected);

    const rootClass = classNames('gds-search-select', {
        'gds-search-select--open': isOpen,
        [`gds-search-select--${context}`]: context,
        'gds-search-select--inline': inline,
    });

    const inputClasses = classNames('gds-search-select__input', {
        [`gds-search-select__input--${size}`]: size,
        'gds-search-select__input--has-tag': selectedCount > 0,
    });

    const tagHolderClasses = classNames(
        'gds-search-select__tag-holder',
        'gds-search-select__tag-holder--bubble',
        {
            'gds-search-select__tag-holder--bubble-active':
                isTagsOpen && selectedCount > 0,
        },
    );

    const tagSize = size === 'sm' ? 'xs' : 'sm';
    const tagStyle = size === 'sm' ? xsTagFix : smTagFix;

    let notAvalOptions = true;

    return (
        <div ref={container} className={rootClass}>
            <div className="gds-search-select__control">
                {selectedCount > 0 && (
                    <Tag
                        className="gds-search-select__tag-indicator"
                        context={context || 'primary'}
                        hasOption
                        onClick={_toggleTags}
                        onOptionClick={_clearAll}
                        optionIcon="bt-times"
                        optionLabel="Clear all tags"
                        size={tagSize}
                        style={tagStyle}
                        text={selectedCount}
                        disabled={disabled}
                    />
                )}
                <input
                    onFocus={_openSelect}
                    onClick={_openSelect}
                    onChange={_updateSearchTerm}
                    type="text"
                    style={inputFix}
                    className={inputClasses}
                    placeholder={placeholder}
                    value={searchTerm}
                />
                <button
                    className="gds-search-select__toggle-button -cursor--pointer"
                    onClick={_toggleSelect}
                    type="button"
                />
            </div>
            <div className={tagHolderClasses}>
                <div className="gds-search-select__tag-overflow">
                    {options.map(o => {
                        const value = o[valueKey];
                        const label = o[labelKey];
                        const parentValue = value;
                        const { nested = [] } = o;

                        if (!selected[value] && nested.length === 0) return;
                        return (
                            <React.Fragment key={value}>
                                {nested.length === 0 && (
                                    <Tag
                                        key={value}
                                        className="-m-a-1"
                                        context={context || 'primary'}
                                        hasOption
                                        onOptionClick={e => {
                                            e.stopPropagation();
                                            _toggleOption(value, {
                                                hasChildren: !!nested.length,
                                                nested,
                                            });
                                        }}
                                        optionIcon="bt-times"
                                        optionLabel="Remove option"
                                        size="sm"
                                        text={label}
                                        disabled={disabled}
                                    />
                                )}
                                {nested.map(n => {
                                    const value = n[valueKey];
                                    const label = n[labelKey];

                                    const fakeId = `${parentValue}-${value}`;
                                    const isSelected = !!selected[fakeId];
                                    const toggleOption = () =>
                                        _toggleNestedOption(value, parentValue);

                                    if (!isSelected) return;

                                    return (
                                        <Tag
                                            key={value}
                                            className="-m-a-1"
                                            context={context || 'primary'}
                                            hasOption
                                            onOptionClick={toggleOption}
                                            optionIcon="bt-times"
                                            optionLabel="Remove option"
                                            size="sm"
                                            text={label}
                                            disabled={disabled}
                                        />
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>
            <div className="gds-search-select__menu --nested-menu">
                <div className="gds-search-select__menu-items">
                    {!searchTerm && (
                        <div
                            onClick={_selectAll}
                            className="gds-search-select__menu-item"
                            style={{ borderBottom: '1px solid #e5e5e5' }}
                        >
                            Select All
                        </div>
                    )}
                    {!loading &&
                        options.map(option => {
                            const value = option[valueKey];
                            const label = option[labelKey];
                            const parentValue = value;
                            const { nested = [] } = option;
                            const allSelected =
                                nestedCount[value] === nested.length;

                            const itMatches = filter(
                                searchTerm,
                                label,
                                value,
                                option,
                            );

                            let hasNestedMatching = false;

                            const nestedMatchingMap = nested.reduce(
                                (acc, { label, value }) => {
                                    const doesMatch = filter(
                                        searchTerm,
                                        label,
                                        value,
                                    );

                                    if (doesMatch) hasNestedMatching = true;
                                    acc[value] = doesMatch;

                                    return acc;
                                },
                                {},
                            );

                            if (!(itMatches || hasNestedMatching)) return null;

                            const isSelected = nested.length
                                ? allSelected
                                : !!selected[value];

                            notAvalOptions = false;
                            const _isExpanded = isExpanded[value];
                            const showNested =
                                !expandable || searchTerm !== '' || _isExpanded;

                            return (
                                <React.Fragment key={value}>
                                    <div
                                        onClick={e => {
                                            e.stopPropagation();
                                            _toggleOption(value, {
                                                hasChildren: !!nested.length,
                                                nested,
                                            });
                                        }}
                                        className="gds-search-select__menu-item"
                                    >
                                        <label className="gds-search-select__checkbox">
                                            <input
                                                name={label}
                                                className="gds-search-select__checkbox-input"
                                                type="checkbox"
                                                checked={isSelected}
                                                autoComplete="off"
                                                readOnly
                                            />
                                            <span className="gds-search-select__checkbox-indicator" />
                                        </label>
                                        {label}
                                        {expandable && (
                                            <i
                                                className={`fas fa-caret-${
                                                    _isExpanded
                                                        ? 'down'
                                                        : 'right'
                                                } fa-lg --nested-select-caret -p-v-1 -p-h-2`}
                                                onClick={_toggleExpanded(value)}
                                            />
                                        )}
                                    </div>

                                    {showNested &&
                                        nested.map(nestedOption => {
                                            const value =
                                                nestedOption[valueKey];
                                            const label =
                                                nestedOption[labelKey];

                                            const fakeId = `${parentValue}-${value}`;
                                            const isSelected =
                                                !!selected[fakeId];

                                            const toggleOption = () =>
                                                _toggleNestedOption(
                                                    value,
                                                    parentValue,
                                                );

                                            const itMatches =
                                                nestedMatchingMap[value];

                                            if (!itMatches) return;

                                            return (
                                                <div
                                                    key={value}
                                                    onClick={toggleOption}
                                                    className="gds-search-select__menu-item -m-l-4 --nested-option"
                                                >
                                                    <label className="gds-search-select__checkbox">
                                                        <input
                                                            name={label}
                                                            className="gds-search-select__checkbox-input"
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            autoComplete="off"
                                                            readOnly
                                                        />
                                                        <span className="gds-search-select__checkbox-indicator" />
                                                    </label>
                                                    {label}
                                                </div>
                                            );
                                        })}
                                </React.Fragment>
                            );
                        })}
                    {notAvalOptions && !loading && (
                        <div className="gds-search-select__menu-item">
                            <i className="fa fa-fw fa-exclamation-triangle -color-tx-war -m-r-1" />
                            No options available
                        </div>
                    )}
                    {loading && (
                        <div className="gds-search-select__menu-item">
                            <i className="fa fa-fw fa-spinner fa-spin -m-r-1" />
                            Loading ...
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

NestedSelect.propTypes = {
    /** Options available on the dropdown, by default it expects a 'value', and 'label' key,
     * however you can change that thru the props `valueKey` and `labelKey`*/
    options: PropTypes.arrayOf(PropTypes.object).isRequired,
    /** Object that holds the selected items, it looks like: { 1: true, 2: false }.
     * Keys are the 'value' properties on your option. You don't have to setup the `false` options.
     * It is assumed by default to be false */
    selected: PropTypes.object.isRequired,
    /** Returns an object with the selected options, you must store it and pass it back on the `selected` prop */
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    /** Callback that returns the typed text, useful if you need to query options on an endpoint */
    onTextChange: PropTypes.func,
    /** Defaults to 'value', this is also used as a key to uniquely identify an option, it errors out if duplicated. */
    valueKey: PropTypes.string,
    /** Displayed text, string, node on the dropdown. It can also hold components. */
    labelKey: PropTypes.node,
    /** Function used to filter options it receives the search term, label, value, and option itself.
     * Defaults to only filter based on label. Recommended if you use Components for your labels */
    filter: PropTypes.func,
    /** Color of the component */
    context: PropTypes.oneOf(['success', 'warning', 'danger']),
    /** Text to be displayed on the input as a placeholder */
    placeholder: PropTypes.string,
    /** Component's input size */
    size: PropTypes.oneOf(['sm', 'md']),
    /** Bool to indicate the data inside the component is loading */
    loading: PropTypes.bool,
    /** Bool to indicate that the component needs to be rendered inline */
    inline: PropTypes.bool,
    disabled: PropTypes.bool,
    expandable: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    /** When true, clears the search input on select/close. Mirrors TreeSelect usage */
    autoClearSearchValue: PropTypes.bool,
};

export default NestedSelect;
